"use strict";

/**
 * SuperManualInterestImportController (WEB-3656)
 *
 * Drives the SS Super Admin "Import Manual Interest" screen. Lets a Super Admin
 * pick a property, upload a normalized Excel/CSV template of historical manual
 * late-payment interest, preview the outcome via a dry-run, then commit.
 *
 * Both preview and commit hit the same endpoint
 * `sec/super/properties/{property_id}/manual-interest-import` (multipart `file`
 * + boolean `dry_run`), which returns a structured summary:
 *   { total, inserted, overridden, skipped_unmatched_otp, skipped_no_payment,
 *     excluded_non_positive, errors: [{ row, reason }] }
 *
 * Access is gated by the SS_SALES_PAYMENTS_INTEREST_IMPORT permission both here
 * (UI) and server-side.
 */
class SuperManualInterestImportController {

    constructor($scope,
                $timeout,
                $window,
                Upload,
                SSHttpService,
                SSUtilService,
                SSAlertService,
                SSUserService,
                SSConfigService,
                SSConfirmationService
    ) {
        const self = this;

        self.$scope = $scope;
        self.$timeout = $timeout;
        self.$window = $window;
        self.Upload = Upload;

        self.SSHttpService = SSHttpService;
        self.SSUtilService = SSUtilService;
        self.SSAlertService = SSAlertService;
        self.SSUserService = SSUserService;
        self.SSConfigService = SSConfigService;
        self.SSConfirmationService = SSConfirmationService;

        self.data = SHOWSUITE.data;

        // Permission that gates this whole screen (UI + server).
        self.PERMISSION_IMPORT = 'SS_SALES_PAYMENTS_INTEREST_IMPORT';

        // Client-side file guards.
        self.ALLOWED_EXTENSIONS = ['xlsx', 'csv'];
        self.MAX_FILE_SIZE_MB = 10;

        // Template column reference (kept in sync with the server importer).
        self.TEMPLATE_COLUMNS = ['otp_no', 'payment_type_name', 'value_date', 'daily_interest'];

        self.has_access = false;
        if (self.SSUserService.isSuper() && self.SSUserService.hasPermission(self.PERMISSION_IMPORT)) {
            self.has_access = true;
            self.init();
        }
    }

    init() {
        const self = this;

        self.properties = [];
        self.property_id = null;

        self.importFile = null;
        self.fileError = null;

        self.preview = null;        // dry-run summary
        self.result = null;         // committed summary
        self.uploading = false;

        self.getProperties();
    }

    /**
     * Load the list of properties the Super Admin may import against.
     */
    getProperties() {
        const self = this;

        let url = 'sec/super/properties';

        loading(true);
        self.SSHttpService.getAPIRequest(url).then(function (response) {
            loading(false);

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            if (response.data && response.data.properties) {
                self.properties = response.data.properties;
            }

            self.$timeout(function () {
                jQuery('#mii-property-select').niceSelect('update');
            });
        });
    }

    /**
     * Any change to the chosen property invalidates a prior preview/result, so
     * the user must re-preview before committing the (different) scope.
     */
    onPropertyChange() {
        const self = this;

        self.preview = null;
        self.result = null;
    }

    /**
     * Bound to ngf-select. Validates type/size before accepting the file and
     * clears any stale preview/result.
     *
     * @param {File} file
     */
    selectFile(file) {
        const self = this;

        self.preview = null;
        self.result = null;
        self.fileError = null;
        self.importFile = null;

        if (!file) {
            return;
        }

        if (!self.isValidFile(file)) {
            return;
        }

        self.importFile = file;
    }

    /**
     * @param {File} file
     * @returns {boolean} true when the file passes extension + size guards.
     */
    isValidFile(file) {
        const self = this;

        let name = (file.name || '').toLowerCase();
        let ext = name.indexOf('.') >= 0 ? name.split('.').pop() : '';

        if (self.ALLOWED_EXTENSIONS.indexOf(ext) === -1) {
            self.fileError = 'Unsupported file type. Please upload an .xlsx or .csv file.';
            return false;
        }

        if (file.size > self.MAX_FILE_SIZE_MB * 1024 * 1024) {
            self.fileError = 'File is too large. Maximum allowed size is ' + self.MAX_FILE_SIZE_MB + ' MB.';
            return false;
        }

        return true;
    }

    /**
     * @returns {boolean} true when a property and a valid file are both selected.
     */
    canSubmit() {
        const self = this;

        return !!(self.property_id && self.importFile && !self.fileError && !self.uploading);
    }

    /**
     * Dry-run preview: parse/resolve/validate without persisting.
     */
    previewImport() {
        const self = this;

        if (!self.canSubmit()) {
            return;
        }

        self._upload(true, function (summary) {
            self.preview = summary;
            self.result = null;
            self.SSAlertService.success('Preview ready', 'Review the summary below, then confirm to import.');
        });
    }

    /**
     * Commit: persist the rows after explicit confirmation.
     */
    commitImport() {
        const self = this;

        if (!self.canSubmit()) {
            return;
        }

        self.SSConfirmationService.getConfirmed(
            'Import manual interest?',
            'This will insert/override manual interest entries for the selected property. '
            + 'Existing manual entries for the same payment and date will be overwritten. '
            + 'Do you want to proceed?',
            self.SSConfirmationService.TYPE_OK_CANCEL,
            function () {
                self._upload(false, function (summary) {
                    self.result = summary;
                    // Drop the now-stale dry-run preview so only the committed
                    // result shows, and re-disable Import until a fresh preview.
                    self.preview = null;
                    self.SSAlertService.success('Import complete', 'The manual interest entries have been imported.');
                });
            }
        );
    }

    /**
     * Upload the file to the import endpoint.
     *
     * @param {boolean} dryRun  true = preview only, false = commit.
     * @param {function} onSuccess  receives the parsed summary object.
     */
    _upload(dryRun, onSuccess) {
        const self = this;

        if (!self.property_id || !self.importFile) {
            return;
        }

        let url = 'sec/super/properties/' + self.property_id + '/manual-interest-import';

        self.uploading = true;
        loading(true);
        self.Upload.upload({
            url: self.SSHttpService.getApiURL(url),
            method: 'POST',
            data: {
                file: self.importFile,
                dry_run: dryRun ? 1 : 0
            },
            withCredentials: true
        }).then(
            function (response) {
                loading(false);
                self.uploading = false;

                let body = response.data ? response.data : {};

                if (body.code !== 200) {
                    self.SSAlertService.parseAndDisplayError(body);
                    return;
                }

                onSuccess(body.data || {});
            },
            function (response) {
                loading(false);
                self.uploading = false;
                self.SSAlertService.parseAndDisplayError(response);
            }
        );
    }

    /**
     * @param {object} summary
     * @returns {boolean} true when the summary carries per-row errors.
     */
    hasErrors(summary) {
        return !!(summary && summary.errors && summary.errors.length);
    }

    /**
     * Build and download a CSV template with the expected heading row and one
     * sample row, so the user always has a correctly-shaped starting point.
     */
    downloadTemplate() {
        const self = this;

        let header = self.TEMPLATE_COLUMNS.join(',');
        let sample = ['LCR0001', 'S&P', '2024-01-15', '123.45'].map(self._csvCell).join(',');
        let csv = header + '\n' + sample + '\n';

        self._downloadCsv('manual_interest_import_template.csv', csv);
    }

    /**
     * Export the per-row errors of a summary to a CSV file for offline review.
     *
     * @param {object} summary  a preview/commit summary carrying `errors`.
     */
    exportErrors(summary) {
        const self = this;

        if (!self.hasErrors(summary)) {
            return;
        }

        let rows = ['row,reason'];
        jQuery.each(summary.errors, function (i, err) {
            rows.push(self._csvCell(err.row) + ',' + self._csvCell(err.reason));
        });

        self._downloadCsv('manual_interest_import_errors.csv', rows.join('\n') + '\n');
    }

    /**
     * Escape a single CSV cell (wraps in quotes when it contains a comma,
     * quote, or newline; doubles embedded quotes).
     *
     * @param {*} value
     * @returns {string}
     */
    _csvCell(value) {
        let str = (value === null || value === undefined) ? '' : String(value);
        if (/[",\n\r]/.test(str)) {
            str = '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
    }

    /**
     * Trigger a client-side download of CSV text.
     *
     * @param {string} filename
     * @param {string} csv
     */
    _downloadCsv(filename, csv) {
        const self = this;

        let blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
        let downloadUrl = (self.$window.URL || self.$window.webkitURL).createObjectURL(blob);

        let link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        self.$timeout(function () {
            (self.$window.URL || self.$window.webkitURL).revokeObjectURL(downloadUrl);
        }, 0);
    }

    goBack() {
        const self = this;

        self.$window.location.href = '/super/dashboard';
    }

}

SuperManualInterestImportController.$inject = [
    '$scope',
    '$timeout',
    '$window',
    'Upload',
    'SSHttpService',
    'SSUtilService',
    'SSAlertService',
    'SSUserService',
    'SSConfigService',
    'SSConfirmationService'
];
app.controller('SuperManualInterestImportController', SuperManualInterestImportController);
